/** Currency + number formatting shared by the wizard, the review step and the quote. */

const ZAR = new Intl.NumberFormat("en-ZA", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
  useGrouping: true,
});

/** "R1,234" — matches the server-side formatter exactly (commas, no cents). */
export function money(value, { suffix = "" } = {}) {
  if (value === null || value === undefined || value === "") return "";
  const number = Number(value);
  if (Number.isNaN(number)) return "";
  const negative = number < 0;
  const abs = Math.abs(number);
  const rounded = Math.round(abs);
  const body =
    Math.abs(abs - rounded) < 0.005
      ? ZAR.format(rounded).replace(/\u00a0/g, ",").replace(/\s/g, ",")
      : abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${negative ? "-R" : "R"}${body}${suffix || ""}`;
}

/** Group digits as the customer types: "3200000" -> "3,200,000" */
export function groupDigits(raw) {
  const text = String(raw ?? "");
  const negative = text.trim().startsWith("-");
  const cleaned = text.replace(/[^0-9.]/g, "");
  if (cleaned === "") return negative ? "-" : "";
  const [whole, ...rest] = cleaned.split(".");
  const grouped = (whole || "0").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const decimals = rest.length ? `.${rest.join("").slice(0, 2)}` : text.includes(".") ? "." : "";
  return `${negative ? "-" : ""}${grouped}${decimals}`;
}

/** Strip formatting back to a number. Returns null for blank / unparseable. */
export function parseNumber(raw) {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  const cleaned = String(raw).replace(/[^0-9.\-]/g, "");
  if (cleaned === "" || cleaned === "-" || cleaned === "." || cleaned === "-.") return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

export function formatCount(value) {
  const number = parseNumber(value);
  if (number === null) return "";
  return ZAR.format(Math.round(number)).replace(/\u00a0/g, ",").replace(/\s/g, ",");
}

export function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
}

export function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.toLocaleDateString("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })}, ${date.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}`;
}

export function relativeTime(value) {
  if (!value) return "";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Date.now() - then;
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  if (days < 31) return `${days} day${days === 1 ? "" : "s"} ago`;
  return formatDate(value);
}
